/** A pseudo-random source in the half-open interval [0, 1). */
export interface Rng {
  next(): number;
}

/** mulberry32 — tiny, fast, deterministic 32-bit PRNG. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return {
    next(): number {
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    },
  };
}

/** FNV-1a hash of a string → unsigned 32-bit seed. Stable across runs. */
export function hashSeed(seed?: string | null): number {
  const s = seed ?? '';
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Build an Rng from an optional seed. When `seed` is empty/nullish, falls back
 * to `Math.random()` so repeated calls vary (used for "reroll" with no seed).
 * The `salt` decorrelates two streams derived from the same seed (e.g. names
 * vs. avatars).
 */
export function seededRng(seed?: string | null, salt = ''): Rng {
  const base =
    seed != null && seed !== ''
      ? hashSeed(seed + salt)
      : (Math.random() * 0x1_0000_0000) >>> 0;
  return mulberry32(base);
}
